import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTheme } from '../theme/ThemeContext';
import {
  SATELLITE_LABELS,
  type IndexMapSatellite,
  type IndexMapSatelliteFilter,
} from '../constants/indexMapSatellites';
import { defaultSatelliteForIndex } from '../constants/indexMapSatellites';

/** Observation scene card for the heatmap date bar. */
export interface HeatMapObservationScene {
  id: string;
  date: string; // YYYY-MM-DD
  datetime?: string; // ISO or display time
  satellite: IndexMapSatellite | string;
  /**
   * Whole-tile / scene cloud cover % from observation-date API when available.
   */
  tileCloudCover?: number | null;
}

export type HeatMapSatelliteOption = IndexMapSatelliteFilter;

/** @deprecated use defaultSatelliteForIndex from indexMapSatellites */
export function heatMapSatelliteForIndex(index: string): HeatMapSatelliteOption {
  return defaultSatelliteForIndex(index);
}

/** Local calendar YYYY-MM-DD (avoids UTC shift from toISOString). */
export function formatLocalYmd(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function addMonthsLocal(d: Date, months: number): Date {
  const next = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  next.setMonth(next.getMonth() + months);
  return next;
}

export function addDaysLocal(d: Date, days: number): Date {
  const next = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  next.setDate(next.getDate() + days);
  return next;
}

/** Inclusive 2-month lookback ending at `end` (default: today). */
export function getTwoMonthWindowEnding(end: Date = new Date()): { start_date: string; end_date: string } {
  const endLocal = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  const startLocal = addMonthsLocal(endLocal, -2);
  return {
    start_date: formatLocalYmd(startLocal),
    end_date: formatLocalYmd(endLocal),
  };
}

/** Next older 2-month window before the oldest already-loaded date. */
export function getPreviousTwoMonthWindow(oldestLoadedDate: string): {
  start_date: string;
  end_date: string;
} {
  const oldest = new Date(`${oldestLoadedDate}T00:00:00`);
  const endLocal = addDaysLocal(oldest, -1);
  const startLocal = addMonthsLocal(endLocal, -2);
  return {
    start_date: formatLocalYmd(startLocal),
    end_date: formatLocalYmd(endLocal),
  };
}

export function datesToHeatMapScenes(
  dates: string[],
  satellite: HeatMapSatelliteOption = 'S2'
): HeatMapObservationScene[] {
  const unique = Array.from(new Set(dates.filter(Boolean)));
  // Newest first for the horizontal bar
  unique.sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));
  return unique.map((date) => ({
    id: `${satellite}-${date}`,
    date,
    satellite,
    tileCloudCover: null,
  }));
}

export interface Sentinel2ObservationApiRow {
  date: string;
  satellite?: string | null;
  tile_cloud_percentage?: number | null;
  cloud_percentage?: number | null;
}

/** Map `/sentinel2observationdate/` observations (with cloud) into bar cards. */
export function observationsToHeatMapScenes(
  observations: Sentinel2ObservationApiRow[],
  satellite: HeatMapSatelliteOption = 'S2'
): HeatMapObservationScene[] {
  type Acc = { date: string; cloud: number | null; satellite: string };
  const byKey = new Map<string, Acc>();

  for (const row of observations) {
    if (!row?.date) continue;
    const sat = (row.satellite || (satellite === 'ALL' ? 'S2' : satellite) || 'S2').toUpperCase();
    const key = `${sat}-${row.date}`;
    const cloudRaw =
      row.tile_cloud_percentage != null
        ? row.tile_cloud_percentage
        : row.cloud_percentage != null
          ? row.cloud_percentage
          : null;
    const cloud =
      cloudRaw == null || Number.isNaN(Number(cloudRaw)) ? null : Number(cloudRaw);
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, { date: row.date, cloud, satellite: sat });
    } else if (cloud != null && (existing.cloud == null || cloud < existing.cloud)) {
      byKey.set(key, { date: row.date, cloud, satellite: sat });
    }
  }

  const scenes: HeatMapObservationScene[] = Array.from(byKey.entries()).map(([id, acc]) => ({
    id,
    date: acc.date,
    satellite: acc.satellite,
    tileCloudCover: acc.cloud,
  }));

  // Newest date first; S2 before L8 on same date for stable order
  scenes.sort((a, b) => {
    if (a.date !== b.date) return a.date < b.date ? 1 : -1;
    return String(a.satellite).localeCompare(String(b.satellite));
  });
  return scenes;
}

/** Prefer `observations` (with cloud); fall back to flat `dates`. */
export function parseSentinel2ObservationResponse(
  data: {
    observations?: Sentinel2ObservationApiRow[];
    dates?: string[];
  } | null | undefined,
  satellite: HeatMapSatelliteOption = 'S2'
): HeatMapObservationScene[] {
  if (data?.observations?.length) {
    return observationsToHeatMapScenes(data.observations, satellite);
  }
  return datesToHeatMapScenes(data?.dates ?? [], satellite);
}

function formatCardDate(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00`);
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatCardTime(datetime?: string): string {
  if (!datetime) return '';
  const d = new Date(datetime);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

function cloudBadgeClass(cloud: number | null | undefined, isDark: boolean): string {
  if (cloud == null) {
    return isDark
      ? 'bg-gray-800 text-gray-400 border-gray-600'
      : 'bg-gray-100 text-gray-500 border-gray-200';
  }
  if (cloud <= 20) {
    return isDark
      ? 'bg-[rgba(52,211,153,.16)] text-[#34d399] border-[#34d399]/40'
      : 'bg-[#00854014] text-[#008540] border-[#00854080]';
  }
  if (cloud <= 50) {
    return isDark
      ? 'bg-amber-900/50 text-amber-300 border-amber-600/50'
      : 'bg-amber-50 text-amber-800 border-amber-200';
  }
  return isDark
    ? 'bg-rose-900/50 text-rose-300 border-rose-600/50'
    : 'bg-rose-50 text-rose-700 border-rose-200';
}

export interface HeatMapObservationBarProps {
  observations: HeatMapObservationScene[];
  selectedId: string | null;
  onSelect: (scene: HeatMapObservationScene) => void;
  satellite: HeatMapSatelliteOption;
  onSatelliteChange: (satellite: HeatMapSatelliteOption) => void;
  /** Which satellites are selectable (from current index). Default: all three. */
  allowedSatellites?: HeatMapSatelliteOption[];
  cloudFilterEnabled: boolean;
  onCloudFilterEnabledChange: (enabled: boolean) => void;
  maxCloudCover: number;
  onMaxCloudCoverChange: (value: number) => void;
  /** Called when user scrolls near the older end — parent can append more dates. */
  onLoadMore?: () => void;
  loadingMore?: boolean;
  loadingInitial?: boolean;
  className?: string;
  /** Index Map walk mode (only when a clear/non-masked map is loaded). */
  walkAvailable?: boolean;
  isWalking?: boolean;
  onWalkToggle?: () => void;
  onWalkInfo?: () => void;
}

export default function HeatMapObservationBar({
  observations,
  selectedId,
  onSelect,
  satellite,
  onSatelliteChange,
  allowedSatellites = ['ALL', 'S2', 'L8', 'S1'],
  cloudFilterEnabled,
  onCloudFilterEnabledChange,
  maxCloudCover,
  onMaxCloudCoverChange,
  onLoadMore,
  loadingMore = false,
  loadingInitial = false,
  className = '',
  walkAvailable = false,
  isWalking = false,
  onWalkToggle,
  onWalkInfo,
}: HeatMapObservationBarProps) {
  const { isDark } = useTheme();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const loadMoreLockRef = useRef(false);

  const showCloudFilter = satellite === 'S2' || satellite === 'L8' || satellite === 'ALL';

  const filtered = useMemo(() => {
    if (!cloudFilterEnabled || !showCloudFilter) return observations;
    return observations.filter((o) => {
      if (o.tileCloudCover == null) return true;
      return o.tileCloudCover <= maxCloudCover;
    });
  }, [observations, showCloudFilter, cloudFilterEnabled, maxCloudCover]);

  const handleScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el || !onLoadMore || loadingMore || loadMoreLockRef.current) return;
    // Horizontal: near the trailing edge (older scenes)
    const remaining = el.scrollWidth - el.scrollLeft - el.clientWidth;
    if (remaining < 120) {
      loadMoreLockRef.current = true;
      onLoadMore();
      window.setTimeout(() => {
        loadMoreLockRef.current = false;
      }, 800);
    }
  }, [onLoadMore, loadingMore]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const shell = isDark
    ? 'bg-[rgba(22,27,34,.95)] border-0 text-[#e6edf3]'
    : 'bg-[rgba(248,245,236,.96)] border-0 text-[#111827]';
  const muted = isDark ? 'text-[#8b949e]' : 'text-[#6b7280]';
  const label = isDark ? 'text-[#c9d1d9]' : 'text-[#6b7280]';
  const control = isDark
    ? 'bg-[#1c2432] border-[#2d3748] text-[#e6edf3]'
    : 'bg-white border-[#e5e7eb] text-[#111827]';
  const cardIdle = isDark
    ? 'bg-[#1c2432] border-[#2d3748] hover:border-[#34d399]/50'
    : 'bg-white border-[#e5e7eb] hover:border-[#008540]/50';
  const cardActive = isDark
    ? 'bg-[rgba(52,211,153,.16)] border-[#34d399] ring-1 ring-[#34d399]/35'
    : 'bg-[#00854014] border-[#008540] ring-1 ring-[#008540]/30';
  const skeletonBase = isDark
    ? 'bg-[#1c2432] border-[#2d3748]'
    : 'bg-[#f3f4f6] border-[#e5e7eb]';
  const skeletonPulse = isDark ? 'bg-[#253347]' : 'bg-[#e5e7eb]';

  const skeletonCount = 6;

  const renderSkeletonCard = (key: string | number, delayMs = 0) => (
    <div
      key={key}
      className={`shrink-0 w-[148px] rounded-lg border px-2.5 py-2 overflow-hidden relative ${skeletonBase}`}
      style={{ animationDelay: `${delayMs}ms` }}
      aria-hidden
    >
      {/* Blurred ghost content */}
      <div className="blur-[2.5px] opacity-70 select-none pointer-events-none">
        <div className="flex items-center justify-between gap-1 mb-1">
          <div className={`h-3 w-8 rounded ${skeletonPulse}`} />
          <div className={`h-4 w-12 rounded ${skeletonPulse}`} />
        </div>
        <div className={`h-3.5 w-24 rounded mb-1 ${skeletonPulse}`} />
        <div className={`h-2.5 w-16 rounded mb-2 ${skeletonPulse}`} />
        <div className={`h-2 w-14 rounded ${skeletonPulse}`} />
      </div>
      {/* Shimmer sweep */}
      <div
        className={`absolute inset-0 -translate-x-full animate-[hmObsShimmer_1.35s_ease-in-out_infinite] bg-gradient-to-r from-transparent to-transparent ${
          isDark ? 'via-white/10' : 'via-white/55'
        }`}
        style={{ animationDelay: `${delayMs}ms` }}
      />
    </div>
  );

  return (
    <div
      className={`absolute bottom-3 left-14 right-3 sm:left-[4.5rem] sm:right-[6rem] z-10 rounded-[14px] shadow-[0_1px_2px_rgba(0,0,0,.4),0_10px_26px_-14px_rgba(0,0,0,.7)] backdrop-blur-[6px] ${shell} ${className}`}
      role="region"
      aria-label="Index map observation dates"
    >
      <style>{`
        @keyframes hmObsShimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes hmObsCardIn {
          from { opacity: 0; filter: blur(4px); transform: translateY(4px) scale(0.98); }
          to { opacity: 1; filter: blur(0); transform: translateY(0) scale(1); }
        }
        .hm-obs-card-enter {
          animation: hmObsCardIn 0.32s ease-out both;
        }
      `}</style>
      <div className="px-3 pt-2.5 pb-2 space-y-2">
        {/* Top controls: satellite + cloud filter */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <div className="flex items-center gap-2 min-w-0">
            <i className={`ri-satellite-line ${isDark ? 'text-[#34d399]' : 'text-[#008540]'}`} />
            <label className={`text-[11px] font-medium whitespace-nowrap ${label}`}>Satellite</label>
            <select
              value={satellite}
              onChange={(e) => onSatelliteChange(e.target.value as HeatMapSatelliteOption)}
              className={`text-xs rounded-md border px-2 py-1 focus:outline-none focus:ring-1 ${isDark ? 'focus:ring-[#34d399]' : 'focus:ring-[#008540]'} ${control}`}
              aria-label="Index map satellite"
            >
              {allowedSatellites.map((code) => (
                <option key={code} value={code}>
                  {SATELLITE_LABELS[code] ?? code}
                </option>
              ))}
            </select>
          </div>

          {showCloudFilter && (
          <div className="flex items-center gap-2 flex-1 min-w-[180px] max-w-md">
            <button
              type="button"
              onClick={() => onCloudFilterEnabledChange(!cloudFilterEnabled)}
              className={`text-[10px] font-semibold px-2 py-1 rounded-md border transition-colors ${
                cloudFilterEnabled
                  ? isDark
                    ? 'bg-sky-900/50 border-sky-500 text-sky-300'
                    : 'bg-sky-50 border-sky-400 text-sky-800'
                  : isDark
                    ? 'bg-gray-800 border-gray-600 text-gray-400'
                    : 'bg-gray-50 border-gray-300 text-gray-500'
              }`}
              title="Filter observation cards by scene cloud cover"
            >
              Cloud filter {cloudFilterEnabled ? 'ON' : 'OFF'}
            </button>
            <div className={`flex-1 ${cloudFilterEnabled ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
              <div className="flex items-center justify-between mb-0.5">
                <span className={`text-[10px] ${muted}`}>Max scene cloud</span>
                <span className={`text-[10px] font-semibold tabular-nums ${label}`}>{maxCloudCover}%</span>
              </div>
              <input
                type="range"
                min={0}
                max={100}
                step={1}
                value={maxCloudCover}
                disabled={!cloudFilterEnabled}
                onChange={(e) => onMaxCloudCoverChange(Number(e.target.value))}
                className="w-full h-1.5 rounded-lg appearance-none cursor-pointer accent-sky-500 bg-gray-600"
                aria-label="Maximum scene cloud cover"
              />
            </div>
          </div>
          )}
          {satellite === 'S1' && (
            <span className={`text-[10px] ${muted}`}>
              Radar (SAR) — cloud cover does not apply
            </span>
          )}

          {walkAvailable && onWalkToggle && (
            <div className="ml-auto flex items-center gap-1.5">
              {!isWalking && onWalkInfo && (
                <button
                  type="button"
                  onClick={onWalkInfo}
                  className={`h-7 w-7 rounded-md border flex items-center justify-center transition-colors ${
                    isDark
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-800'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                  title="Walk mode goal, objective, and steps"
                  aria-label="Walk mode information"
                >
                  <i className="ri-information-line text-sm" />
                </button>
              )}
              <button
                type="button"
                onClick={onWalkToggle}
                className={`text-[11px] font-semibold px-2.5 py-1 rounded-md border transition-colors ${
                  isWalking
                    ? isDark
                      ? 'bg-rose-900/50 border-rose-500 text-rose-200'
                      : 'bg-rose-50 border-rose-400 text-rose-800'
                    : isDark
                      ? 'bg-[rgba(52,211,153,.16)] border-[#34d399] text-[#34d399]'
                      : 'bg-[#00854014] border-[#008540] text-[#008540]'
                }`}
                title={
                  isWalking
                    ? 'End walk and build a report for Pandoy AI'
                    : 'Walk the field; answer vegetation & moisture as you move'
                }
              >
                <i className={`mr-1 ${isWalking ? 'ri-stop-circle-line' : 'ri-walk-line'}`} />
                {isWalking ? 'End walk' : 'Walk mode'}
              </button>
            </div>
          )}
        </div>

        {showCloudFilter && (
        <p
          className={`text-[10px] leading-snug rounded-md px-2 py-1.5 border ${
            isDark
              ? 'bg-sky-950/40 border-sky-700/40 text-sky-200/90'
              : 'bg-sky-50 border-sky-100 text-sky-900/80'
          }`}
        >
          <i className="ri-information-line mr-1" />
          Cloud % is for the whole optical scene
          {satellite === 'ALL' ? ' (S2 or Landsat)' : ` (${SATELLITE_LABELS[satellite]})`}.
          Your farm AOI may still be
          cloud-free or less cloudy than the scene average.
        </p>
        )}
        {satellite === 'S1' && (
        <p
          className={`text-[10px] leading-snug rounded-md px-2 py-1.5 border ${
            isDark
              ? 'bg-amber-950/40 border-amber-700/40 text-amber-200/90'
              : 'bg-amber-50 border-amber-100 text-amber-900/80'
          }`}
        >
          <i className="ri-information-line mr-1" />
          Showing <strong>Sentinel-1</strong> acquisition dates for radar indices.
        </p>
        )}

        {/* Date cards scroller */}
        <div
          ref={scrollerRef}
          className="flex gap-2 overflow-x-auto pb-1 pt-0.5 scroll-smooth [-ms-overflow-style:none] [scrollbar-width:thin]"
          style={{ scrollbarColor: isDark ? '#4b5563 transparent' : '#d1d5db transparent' }}
          aria-busy={loadingInitial || loadingMore}
        >
          {loadingInitial && filtered.length === 0 && (
            <>
              {Array.from({ length: skeletonCount }, (_, i) =>
                renderSkeletonCard(`skel-init-${i}`, i * 70)
              )}
            </>
          )}

          {!loadingInitial && filtered.length === 0 && (
            <div className={`px-3 py-4 text-xs ${muted}`}>
              {observations.length === 0
                ? 'No observation dates in this window.'
                : 'No scenes match the cloud filter. Raise the max cloud % or turn the filter off.'}
            </div>
          )}

          {filtered.map((scene, idx) => {
            const active = scene.id === selectedId;
            const cloudLabel =
              scene.tileCloudCover == null ? '—' : `${scene.tileCloudCover.toFixed(2)}%`;
            return (
              <button
                key={scene.id}
                type="button"
                onClick={() => onSelect(scene)}
                className={`hm-obs-card-enter shrink-0 w-[148px] rounded-lg border px-2.5 py-2 text-left transition-all ${
                  active ? cardActive : cardIdle
                }`}
                style={{ animationDelay: `${Math.min(idx, 12) * 35}ms` }}
              >
                <div className="flex items-center justify-between gap-1 mb-1">
                  <span
                    className={`text-[10px] font-bold tracking-wide ${
                      isDark ? 'text-[#34d399]' : 'text-[#008540]'
                    }`}
                  >
                    {String(scene.satellite)}
                  </span>
                  <span
                    className={`text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded border ${cloudBadgeClass(
                      scene.tileCloudCover,
                      isDark
                    )}`}
                  >
                    {cloudLabel}
                  </span>
                </div>
                <div className={`text-xs font-semibold leading-tight ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                  {formatCardDate(scene.date)}
                </div>
                {scene.datetime && (
                  <div className={`text-[10px] mt-0.5 tabular-nums ${muted}`}>
                    {formatCardTime(scene.datetime)}
                  </div>
                )}
                <div className={`text-[9px] mt-1 ${muted}`}>
                  {scene.tileCloudCover == null ? 'Tile cloud n/a' : 'Tile cloud'}
                </div>
              </button>
            );
          })}

          {loadingMore && (
            <>
              {Array.from({ length: 3 }, (_, i) =>
                renderSkeletonCard(`skel-more-${i}`, i * 80)
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
